import React, { useState } from "react";
import {
    Button,
    Box,
    Typography,
    TextField,
    Grid,
    MenuItem,
    CircularProgress,
    IconButton,
    Tooltip,
} from "@material-ui/core";
import { FaSearch, FaPaperPlane, FaCopy, FaCheckCircle } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import HttpService from '../../services/httpService';

const SEARCH_TYPES = ["Customer Name", "Email ID", "Lead No", "Project No", "Unique Lead No"];

// Why a link is disabled in the dropdown; only "pending" links can be resent.
const STATUS_SUFFIX = {
    completed: "Already completed",
    notSent: "Not sent yet",
    noEmail: "Email not available",
};

const leadOptionLabel = (lead) => [
    lead?.customerId?.name || "NA",
    `Lead No: ${lead?.lead_no ?? "NA"}`,
    lead?.uniqueLeadNumber,
    lead?.erpProjectNo,
].filter(Boolean).join(" | ");

const getErrorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const ResendCustomerLink = ({ classes, setSnackInfo }) => {
    const [searchType, setSearchType] = useState(SEARCH_TYPES[0]);
    const [search, setSearch] = useState("");
    const [leads, setLeads] = useState(null);
    const [selectedLeadId, setSelectedLeadId] = useState("");
    const [links, setLinks] = useState([]);
    const [selectedLinkKey, setSelectedLinkKey] = useState("");
    const [searching, setSearching] = useState(false);
    const [loadingLinks, setLoadingLinks] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const loadLinks = async (leadId) => {
        setSelectedLeadId(leadId);
        setSelectedLinkKey("");
        setLinks([]);
        if (!leadId) return;
        setLoadingLinks(true);
        try {
            const response = await HttpService.getCustomerLinks(leadId);
            setLinks(response?.data || []);
        } catch (err) {
            setError(getErrorMessage(err, "Failed to load customer links"));
        } finally {
            setLoadingLinks(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        const term = search.trim();
        if (!term) {
            setError(`Please enter ${searchType}`);
            return;
        }
        setSearching(true);
        setError(null);
        setLeads(null);
        setSelectedLeadId("");
        setSelectedLinkKey("");
        setLinks([]);
        try {
            const response = await HttpService.searchLeadsForCustomerLinks(searchType, term);
            const foundLeads = response?.data || [];
            setLeads(foundLeads);
            if (foundLeads.length === 0) {
                setError("No leads found");
            } else if (foundLeads.length === 1) {
                await loadLinks(foundLeads[0]._id);
            }
        } catch (err) {
            const message = getErrorMessage(err, "Failed to search leads");
            setError(message);
            setSnackInfo(message, "error");
        } finally {
            setSearching(false);
        }
    };

    const handleSend = async () => {
        setSending(true);
        setError(null);
        try {
            const response = await HttpService.resendCustomerLink(selectedLeadId, selectedLinkKey);
            setSnackInfo(response?.data?.message || "Link sent successfully", "success");
            await loadLinks(selectedLeadId);
        } catch (err) {
            const message = getErrorMessage(err, "Failed to send link");
            setError(message);
            setSnackInfo(message, "error");
        } finally {
            setSending(false);
        }
    };

    // navigator.clipboard is missing outside https/localhost, so a failed copy
    // shows an error instead of throwing.
    const copyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setSnackInfo("Link copied to clipboard", "success");
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            setSnackInfo("Could not copy the link", "error");
        }
    };

    const handleReset = () => {
        setSearchType(SEARCH_TYPES[0]);
        setSearch("");
        setLeads(null);
        setSelectedLeadId("");
        setSelectedLinkKey("");
        setLinks([]);
        setError(null);
    };

    const selectedLink = links.find(link => link.key === selectedLinkKey);
    const hasNoPendingLink = !loadingLinks && links.length > 0 && links.every(link => link.status !== "pending");

    return (
        <form onSubmit={handleSearch}>
            <Typography className={classes.sectionTitle}>
                <FaSearch className={classes.inputIcon} />
                Search lead to resend a customer link
            </Typography>
            <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                    <TextField
                        select
                        fullWidth
                        label="Search By"
                        value={searchType}
                        onChange={(e) => { setSearchType(e.target.value); setError(null); }}
                        variant="outlined"
                        size="small"
                        className={classes.textField}
                    >
                        {SEARCH_TYPES.map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={5}>
                    <TextField
                        fullWidth
                        label={searchType}
                        value={search}
                        onChange={(e) => { setSearch(e.target.value.trimStart()); setError(null); }}
                        variant="outlined"
                        required
                        size="small"
                        className={classes.textField}
                        placeholder={`Enter ${searchType}`}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Button type="submit" variant="contained" className={classes.submitButton} disabled={searching || sending} fullWidth>
                        {searching ? <CircularProgress size={18} color="inherit" /> : "Search Lead"}
                    </Button>
                </Grid>
            </Grid>

            {leads?.length > 1 && (
                <Box mt={3}>
                    <TextField
                        select
                        fullWidth
                        label={`Select Lead (${leads.length} found)`}
                        value={selectedLeadId}
                        onChange={(e) => loadLinks(e.target.value)}
                        variant="outlined"
                        size="small"
                        className={classes.textField}
                        disabled={loadingLinks || sending}
                    >
                        {leads.map(lead => (
                            <MenuItem key={lead._id} value={lead._id}>{leadOptionLabel(lead)}</MenuItem>
                        ))}
                    </TextField>
                </Box>
            )}

            {leads?.length === 1 && (
                <Typography className={classes.linkLabel} style={{ marginTop: 16 }}>
                    Lead: <strong>{leadOptionLabel(leads[0])}</strong>
                </Typography>
            )}

            {selectedLeadId && (
                <Box mt={3}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={9}>
                            <TextField
                                select
                                fullWidth
                                label={loadingLinks ? "Loading links..." : "Select Link to Resend"}
                                value={selectedLinkKey}
                                onChange={(e) => { setSelectedLinkKey(e.target.value); setError(null); }}
                                variant="outlined"
                                size="small"
                                className={classes.textField}
                                disabled={loadingLinks || sending || links.length === 0}
                            >
                                {links.map(link => (
                                    <MenuItem key={link.key} value={link.key} disabled={link.status !== "pending"}>
                                        {link.status === "pending" ? link.label : `${link.label} (${STATUS_SUFFIX[link.status]})`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="contained"
                                className={classes.submitButton}
                                disabled={!selectedLink || selectedLink.status !== "pending" || sending}
                                onClick={handleSend}
                                startIcon={!sending && <FaPaperPlane size={14} />}
                                fullWidth
                            >
                                {sending ? <CircularProgress size={18} color="inherit" /> : "Send"}
                            </Button>
                        </Grid>
                    </Grid>
                    {selectedLink?.recipient && (
                        <Typography className={classes.linkLabel} style={{ marginTop: 12 }}>
                            Link will be sent to: <strong>{selectedLink.recipient}</strong>
                        </Typography>
                    )}
                    {hasNoPendingLink && (
                        <Typography className={classes.linkLabel} style={{ marginTop: 12 }}>
                            No link is pending with the customer for this lead.
                        </Typography>
                    )}
                </Box>
            )}

            {error && (
                <Box className={classes.errorBox}>
                    <Typography className={classes.errorText}>{error}</Typography>
                </Box>
            )}

            <Box mt={4} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 16 }}>
                <Button variant="outlined" className={classes.resetButton} style={{ marginLeft: 0 }} onClick={handleReset} startIcon={<MdRefresh />} disabled={searching || sending}>
                    Reset
                </Button>
                {selectedLink?.url && (
                    <Box className={classes.linkBox} flex="1 1 300px" minWidth={0}>
                        <Typography className={classes.linkText}>{selectedLink.url}</Typography>
                        <Tooltip title={copied ? "Copied!" : "Copy link"}>
                            <IconButton
                                size="small"
                                className={copied ? classes.copyButtonSuccess : classes.copyButton}
                                onClick={() => copyLink(selectedLink.url)}
                            >
                                {copied ? <FaCheckCircle /> : <FaCopy />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </form>
    );
};

export default ResendCustomerLink;